import { F0Dialog } from '@factorialco/f0-react'

import {
  DuplicateTaskFormDocument,
  TaskFormsDataCollectionDocument,
} from 'generated/resources/graphql'

import { useMutation } from 'modules/core/hooks/useMutation'
import { useActivity } from 'modules/core/lib/activities/activity'
import type { ActivityComponent } from 'modules/core/lib/activities/types'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

interface DuplicateTaskFormData extends Record<string, string> {
  taskFormId: string
  taskFormName: string
}

export const DuplicateTaskFormActivity: ActivityComponent<DuplicateTaskFormData> = ({
  taskFormId,
  taskFormName,
}) => {
  const { open, closeActivity } = useActivity()

  const [duplicateTaskForm, { loading: isDuplicating }] = useMutation(DuplicateTaskFormDocument, {
    extractorFn: (data) => data.procurementMutations.duplicateProcurementTaskForm,
  })

  if (!open) return null

  const onConfirm = async () => {
    const result = await duplicateTaskForm({
      variables: { id: taskFormId },
      refetchQueries: [TaskFormsDataCollectionDocument],
    })

    const handled = handleResult(result, (errors) => {
      const errorMessage = Object.values(errors).filter(Boolean).join(', ')
      app.setFlash({
        type: 'wrong',
        message: errorMessage || i18n.t('errors.generic_error'),
      })
    })

    if (handled.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('common.saved_successfully'),
      })
      closeActivity()
    }
  }

  return (
    <F0Dialog
      isOpen
      onClose={closeActivity}
      title={i18n.t('procurement.purchase_requests.settings.task_forms.duplicate_dialog.title')}
      description={i18n.t(
        'procurement.purchase_requests.settings.task_forms.duplicate_dialog.description',
        { name: taskFormName }
      )}
      primaryAction={{
        label: i18n.t('procurement.purchase_requests.settings.task_forms.duplicate_dialog.confirm'),
        loading: isDuplicating,
        disabled: isDuplicating,
        onClick: onConfirm,
      }}
      secondaryAction={{
        label: i18n.t('procurement.purchase_requests.settings.task_forms.duplicate_dialog.cancel'),
        disabled: isDuplicating,
        onClick: closeActivity,
      }}
    />
  )
}
